import React, { useState } from 'react';
import QRCode from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

// Scoped MUI theme — inherits the app font, no CssBaseline.
const qrTheme = createTheme({
  typography: { fontFamily: 'inherit' },
  palette: { mode: 'light', primary: { main: '#0c0c0c' } },
  shape: { borderRadius: 6 },
});

// Matches the logo-over-QR math used by PrintableCurrentPageQR.
const logoMetrics = size =>
  size < 128
    ? { logoSize: size / 3, logoMargin: size / 3 }
    : { logoSize: size / 4, logoMargin: size / 4 + size / 8 };

const QRWithLogo = ({ size, url, logoImagePath }) => {
  const { logoSize, logoMargin } = logoMetrics(size);
  return (
    <Box sx={{ position: 'relative', width: size, height: size, lineHeight: 0 }}>
      <QRCode value={url} level="H" size={size} renderAs="png" />
      {logoImagePath && (
        <Box
          component="img"
          src={logoImagePath}
          alt=""
          aria-hidden
          sx={{
            position: 'absolute',
            width: logoSize,
            height: logoSize,
            left: logoMargin,
            top: logoMargin,
          }}
        />
      )}
    </Box>
  );
};

const BQRLauncher = ({ title, smallSize = 96, largeSize = 280, logoImagePath }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';

  const launch = () => setOpen(true);
  const close = () => setOpen(false);

  return (
    <ThemeProvider theme={qrTheme}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={t('building.summary.qr.enlarge.label')}
        onClick={launch}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            launch();
          }
        }}
        sx={{
          position: 'relative',
          display: 'inline-block',
          float: 'right',
          cursor: 'zoom-in',
          padding: '4px',
          borderRadius: '6px',
          outline: 'none',
          transition:
            'transform 220ms cubic-bezier(.2,.65,.2,1), box-shadow 220ms ease',
          '&:hover': {
            transform: 'scale(1.04)',
            boxShadow:
              '0 0 0 1px rgba(0,0,0,0.08), 0 8px 22px rgba(0,0,0,0.10)',
          },
          '&:hover .qr-badge': { opacity: 1, transform: 'translate(0, 0)' },
          '&:focus-visible': {
            boxShadow: '0 0 0 2px #0c0c0c',
          },
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
            '&:hover': { transform: 'none' },
          },
        }}
      >
        <QRWithLogo size={smallSize} url={url} logoImagePath={logoImagePath} />
        <Box
          className="qr-badge"
          aria-hidden
          sx={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 22,
            height: 22,
            display: 'grid',
            placeItems: 'center',
            bgcolor: '#0c0c0c',
            color: '#fff',
            borderRadius: '4px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
            opacity: 0.78,
            transform: 'translate(1px, 1px)',
            transition: 'opacity 200ms ease, transform 200ms ease',
            pointerEvents: 'none',
          }}
        >
          <OpenInFullIcon sx={{ fontSize: 12 }} />
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={close}
        PaperProps={{
          sx: {
            bgcolor: '#fff',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: 2,
            boxShadow: '0 30px 60px -20px rgba(0,0,0,0.25)',
            p: { xs: 3, sm: 4.5 },
            m: 2,
            maxWidth: 'min(92vw, 420px)',
            fontFamily: 'inherit',
            position: 'relative',
          },
        }}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(12, 12, 12, 0.42)' },
        }}
      >
        <IconButton
          onClick={close}
          aria-label={t('building.summary.qr.close.label')}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: 'rgba(0, 0, 0, 0.55)',
            '&:hover': { color: '#0c0c0c', bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <QRWithLogo size={largeSize} url={url} logoImagePath={logoImagePath} />
        </Box>
        {title && (
          <Box
            sx={{
              mt: 3,
              textAlign: 'center',
              fontSize: 14,
              fontWeight: 400,
              letterSpacing: '-0.005em',
              lineHeight: 1.4,
              color: '#0c0c0c',
            }}
          >
            {title}
          </Box>
        )}
      </Dialog>
    </ThemeProvider>
  );
};

export default BQRLauncher;
